import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Award, Home, ArrowRight as IconArrowRight, Calendar, IndianRupee,
  Loader2, Search, SlidersHorizontal, X, MapPin, GraduationCap, RotateCcw, Star,
  LayoutGrid, LayoutList
} from "lucide-react";
import { useNavigate, useLocation } from 'react-router-dom';
import CollegeFilter from "./CollegeFilter";
import { getColleges } from "../../services/api";
import { useTheme } from '../Home/ThemeContext';

const initialFilters = {
  disciplines: [], countries: [], continents: [], states: [],
  tuitionFee: { min: "", max: "" },
  durations: [], format: [], attendance: [], degreeTypes: [], specialProgrammes: [],
};

export default function ScholarshipSelectionUI() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useTheme();
  const listRef = useRef(null);

  const [activeTab, setActiveTab] = useState('scholarships');
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState(initialFilters);
  const [apiFilters, setApiFilters] = useState({});
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [viewMode, setViewMode] = useState('list');

  const theme = {
    bg: isDarkMode ? 'bg-slate-950' : 'bg-slate-50',
    sidebar: isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200',
    header: isDarkMode ? 'bg-slate-900/80 border-slate-800' : 'bg-white/80 border-slate-200',
    card: isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200',
    cardHover: isDarkMode ? 'hover:border-yellow-500/50' : 'hover:border-yellow-400',
    textMain: isDarkMode ? 'text-white' : 'text-slate-900',
    textMuted: isDarkMode ? 'text-slate-400' : 'text-slate-500',
    input: isDarkMode ? 'bg-slate-800 border-slate-700 text-white focus:ring-yellow-500' : 'bg-white border-slate-200 text-slate-900 focus:ring-yellow-500',
    accent: 'from-yellow-500 to-orange-500',
    accentText: 'text-yellow-600 dark:text-yellow-400',
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001/api';
    const host = backendUrl.replace('/api', '');
    let cleanedPath = imagePath.replace(/^\/|uploads\//g, '');
    return `${host}/uploads/${cleanedPath}`;
  };

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchQuery), 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
    loadCollegesWithPrograms(1);
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [debouncedSearch, apiFilters]);

  useEffect(() => {
    if (page > 1) loadCollegesWithPrograms(page);
  }, [page]);

  const loadCollegesWithPrograms = async (currentPage) => {
    try {
      setLoading(true);
      const params = { page: currentPage, limit: 10, ...apiFilters, ...(debouncedSearch ? { q: debouncedSearch } : {}) };
      const res = await getColleges(params);
      const backend = res.data;
      const newColleges = backend.data || [];
      setColleges(prev => currentPage === 1 ? newColleges : [...prev, ...newColleges]);
      setTotalPages(backend.totalPages || 1);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
    const params = {};
    if (newFilters.disciplines?.length) params.courses = newFilters.disciplines.join(',');
    if (newFilters.countries?.length) params.countries = newFilters.countries.join(',');
    if (newFilters.states?.length) params.states = newFilters.states.join(',');
    if (newFilters.tuitionFee?.min || newFilters.tuitionFee?.max) {
      params.tuitionFee = `${newFilters.tuitionFee.min || 0},${newFilters.tuitionFee.max || 100000000}`;
    }
    setApiFilters(params);
    setPage(1);
  }, []);

  const handleApplyFilters = useCallback(() => {
    setMobileFilterOpen(false);
  }, []);

  const clearAllFilters = () => {
    setFilters(initialFilters);
    setApiFilters({});
    setSearchQuery('');
    setDebouncedSearch('');
    setPage(1);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'universities') navigate('/student/colleges');
    else if (tab === 'programs') navigate('/student/programs');
  };

  const formatFee = (fee) => {
    if (!fee) return 'N/A';
    const num = parseFloat(String(fee).replace(/[^0-9.]/g, ''));
    if (isNaN(num) || num === 0) return 'N/A';
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
    return `₹${num.toLocaleString('en-IN')}`;
  };

  // Grid Card
  const GridCard = ({ college }) => {
    const logoUrl = getImageUrl(college.logoUrl || college.collegeLogo || college.imageUrl);
    const courses = college.courses || [];
    return (
      <div className={`rounded-2xl border transition-all duration-300 hover:shadow-xl overflow-hidden flex flex-col ${theme.card} ${theme.cardHover}`}>
        <div className={`p-3 border-b ${isDarkMode ? 'border-slate-800' : 'border-slate-100'}`}>
          <div className="w-full h-16 sm:h-20 flex items-center justify-center bg-white rounded-xl mb-2 border border-slate-100 p-2">
            {logoUrl ? (
              <img src={logoUrl} alt={college.collegeName} className="max-w-full max-h-full object-contain"
                onError={e => { e.target.onerror = null; e.target.src = "https://via.placeholder.com/60"; }} />
            ) : <Award className="w-8 h-8 text-yellow-500" />}
          </div>
          <h3 onClick={() => navigate(`/college/${college._id || college.id}`, { state: { from: location.pathname + location.search } })}
            className={`font-bold text-xs text-center line-clamp-2 cursor-pointer hover:text-yellow-600 transition-colors ${theme.textMain}`}>
            {college.collegeName}
          </h3>
          <div className="flex items-center justify-center gap-2 mt-1">
            <span className={`flex items-center gap-1 text-xs ${theme.textMuted}`}>
              <MapPin className="w-3 h-3 text-yellow-500" />
              {college.city}{college.city && college.state ? ', ' : ''}{college.state}
            </span>
            {college.rating && (
              <span className="flex items-center gap-1 text-xs font-bold text-amber-500">
                <Star className="w-3 h-3 fill-current" /> {college.rating}
              </span>
            )}
          </div>
        </div>
        <div className="p-3 flex-1">
          <p className={`text-xs font-bold uppercase tracking-wider mb-2 ${theme.textMuted}`}>
            {courses.length} Program{courses.length !== 1 ? 's' : ''}
          </p>
          <div className="space-y-1.5">
            {courses.slice(0, 2).map((course, idx) => {
              const fee = formatFee(course.Fees || course.fees || course.Fee_INR);
              return (
                <div key={idx} className={`p-2 rounded-lg border text-xs ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                  <p className={`font-semibold truncate ${theme.textMain}`}>{course.name || course.courseName || 'Program'}</p>
                  {fee !== 'N/A' && <p className="text-emerald-600 font-bold">{fee}</p>}
                </div>
              );
            })}
            {courses.length > 2 && (
              <p className={`text-xs font-medium text-center ${theme.textMuted}`}>+{courses.length - 2} more programs</p>
            )}
          </div>
        </div>
        <div className="p-3 pt-0">
          <button onClick={() => navigate(`/college/${college._id || college.id}`, { state: { from: location.pathname + location.search } })}
            className={`w-full py-2 rounded-xl text-xs font-bold border transition-all flex items-center justify-center gap-1 ${theme.card} ${theme.accentText}`}>
            View <IconArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={`h-[calc(100vh-64px)] mt-16 w-full flex flex-col overflow-hidden font-sans ${theme.bg}`}>
      <div className="flex-1 flex overflow-hidden relative">

        {/* SIDEBAR Desktop */}
        <aside className={`hidden lg:block w-64 xl:w-72 h-full flex-none overflow-y-auto custom-scrollbar z-20 border-r ${theme.sidebar}`}>
          <CollegeFilter currentFilters={filters} onFilterChange={handleFilterChange} onClearAll={clearAllFilters} onApplyFilters={handleApplyFilters} />
        </aside>

        {/* MOBILE FILTER DRAWER */}
        {mobileFilterOpen && (
          <div className="fixed inset-0 z-[100] flex justify-end lg:hidden">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setMobileFilterOpen(false)} />
            <div className={`relative w-[90%] max-w-xs h-full shadow-2xl flex flex-col overflow-hidden ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
              <div className={`flex items-center justify-between p-5 border-b ${isDarkMode ? 'border-slate-800' : 'border-slate-100'}`}>
                <h3 className={`text-lg font-bold ${theme.textMain}`}>Filter</h3>
                <button onClick={() => setMobileFilterOpen(false)} className={`p-2 rounded-full ${theme.textMuted}`}><X className="w-6 h-6" /></button>
              </div>
              <div className="flex-1 overflow-y-auto custom-scrollbar p-2">
                <CollegeFilter currentFilters={filters} onFilterChange={handleFilterChange} onClearAll={clearAllFilters} onApplyFilters={handleApplyFilters} />
              </div>
            </div>
          </div>
        )}

        <main className="flex-1 flex flex-col h-full min-w-0 relative">
          {/* STICKY HEADER */}
          <div className={`flex-none z-10 backdrop-blur-md border-b shadow-sm ${theme.header}`}>
            <div className="px-4 sm:px-6 pt-4 sm:pt-5 pb-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <div className="relative flex-1 group">
                <Search className="absolute left-4 top-3.5 w-5 h-5 text-slate-400 group-focus-within:text-yellow-500 transition-colors" />
                <input type="text" placeholder="Search colleges with scholarship programs..."
                  value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                  className={`w-full pl-12 pr-4 py-3 rounded-full border outline-none ring-offset-2 focus:ring-2 transition-all font-medium shadow-sm text-sm sm:text-base ${theme.input}`} />
              </div>
              <div className="flex items-center gap-2">
                <div className={`flex p-1.5 rounded-full ${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'} border border-slate-200/50`}>
                  {['universities', 'programs', 'scholarships'].map(tab => (
                    <button key={tab} onClick={() => handleTabChange(tab)}
                      className={`px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-bold rounded-full transition-all capitalize
                        ${activeTab === tab ? `bg-white dark:bg-slate-700 ${theme.accentText} shadow-md scale-105` : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-300'}`}>
                      {tab}
                    </button>
                  ))}
                </div>
                <div className={`flex p-1 rounded-xl border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-100 border-slate-200'}`}>
                  <button onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white dark:bg-slate-700 shadow text-yellow-600' : 'text-slate-400 hover:text-slate-600'}`}>
                    <LayoutList className="w-4 h-4" />
                  </button>
                  <button onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-white dark:bg-slate-700 shadow text-yellow-600' : 'text-slate-400 hover:text-slate-600'}`}>
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
            <div className="px-4 sm:px-6 pb-4 pt-1 flex items-center justify-between">
              <p className={`text-xs sm:text-sm font-medium ${theme.textMuted}`}>
                Showing programs with scholarship opportunities
              </p>
              <button onClick={() => navigate('/student/dashboard')} className={`p-2.5 rounded-xl border transition-colors ${theme.card} hover:bg-slate-50 dark:hover:bg-slate-700`}>
                <Home className={`w-5 h-5 ${theme.accentText}`} />
              </button>
            </div>
          </div>

          {/* COLLEGE + PROGRAMS LIST */}
          <div ref={listRef} className="flex-1 overflow-y-auto p-3 sm:p-6 lg:p-8 custom-scrollbar">
            {loading && page === 1 ? (
              <div className="flex flex-col items-center justify-center py-32 opacity-70">
                <Loader2 className="w-12 h-12 text-yellow-500 animate-spin mb-4" />
                <p className={`font-medium animate-pulse ${theme.textMuted}`}>Loading scholarship programs...</p>
              </div>
            ) : colleges.length > 0 ? (
              <div className={viewMode === 'grid'
                ? 'grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-5 max-w-7xl mx-auto'
                : 'grid grid-cols-1 gap-4 sm:gap-6 max-w-5xl mx-auto'
              }>
                {colleges.map(college => viewMode === 'grid'
                  ? <GridCard key={college._id || college.id} college={college} />
                  : (
                    // List view
                    <div key={college._id || college.id}
                      className={`rounded-2xl border transition-all duration-300 hover:shadow-xl overflow-hidden ${theme.card} ${theme.cardHover}`}>
                      {/* College Header */}
                      <div className={`flex flex-wrap sm:flex-nowrap items-center gap-3 sm:gap-4 p-4 sm:p-5 border-b ${isDarkMode ? 'border-slate-800 bg-slate-800/50' : 'border-slate-100 bg-slate-50/80'}`}>
                        <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl border bg-white p-1.5 sm:p-2 shadow-sm flex items-center justify-center flex-shrink-0">
                          {getImageUrl(college.logoUrl || college.collegeLogo || college.imageUrl) ? (
                            <img src={getImageUrl(college.logoUrl || college.collegeLogo || college.imageUrl)} alt={college.collegeName}
                              className="max-w-full max-h-full object-contain"
                              onError={e => { e.target.onerror = null; e.target.src = "https://via.placeholder.com/60"; }} />
                          ) : <Award className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-500" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h2 onClick={() => navigate(`/college/${college._id || college.id}`, { state: { from: location.pathname + location.search } })}
                            className={`text-base sm:text-lg font-bold truncate cursor-pointer hover:text-yellow-600 transition-colors ${theme.textMain}`}>
                            {college.collegeName}
                          </h2>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1">
                            <span className={`flex items-center gap-1 text-xs sm:text-sm ${theme.textMuted}`}>
                              <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-yellow-500 flex-shrink-0" />
                              {college.city}{college.city && college.state ? ', ' : ''}{college.state}
                            </span>
                            {college.rating && (
                              <span className="flex items-center gap-1 text-xs sm:text-sm font-bold text-amber-500">
                                <Star className="w-3 h-3 sm:w-3.5 sm:h-3.5 fill-current" /> {college.rating}
                              </span>
                            )}
                          </div>
                        </div>
                        <button onClick={() => navigate(`/college/${college._id || college.id}`, { state: { from: location.pathname + location.search } })}
                          className={`hidden sm:flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold border transition-all hover:-translate-y-0.5 flex-shrink-0 ${theme.card} ${theme.accentText}`}>
                          View <IconArrowRight className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Programs */}
                      {(college.courses || []).length > 0 ? (
                        <div className="p-4 sm:p-5">
                          <p className={`text-xs font-bold uppercase tracking-wider mb-3 sm:mb-4 ${theme.textMuted}`}>
                            {college.courses.length} Program{college.courses.length > 1 ? 's' : ''} Available
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                            {college.courses.map((course, idx) => {
                              const fee = formatFee(course.Fees || course.fees || course.Fee_INR);
                              return (
                                <div key={idx} className={`p-3 sm:p-4 rounded-xl border transition-all hover:shadow-md ${isDarkMode ? 'bg-slate-800 border-slate-700 hover:border-yellow-500/50' : 'bg-white border-slate-200 hover:border-yellow-400'}`}>
                                  <div className="flex items-start gap-2 sm:gap-3">
                                    <div className={`p-1.5 sm:p-2 rounded-lg flex-shrink-0 ${isDarkMode ? 'bg-yellow-900/20' : 'bg-yellow-50'}`}>
                                      <GraduationCap className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-yellow-500" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h4 className={`font-bold text-xs sm:text-sm truncate ${theme.textMain}`}>
                                        {course.name || course.courseName || 'Program'}
                                      </h4>
                                      <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-1.5 sm:mt-2">
                                        {(course.Duration || course.duration) && (
                                          <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                                            <Calendar className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                            {course.Duration || course.duration}
                                          </span>
                                        )}
                                        {fee !== 'N/A' && (
                                          <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-bold ${isDarkMode ? 'bg-emerald-900/30 text-emerald-400' : 'bg-emerald-50 text-emerald-700'}`}>
                                            <IndianRupee className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                            {fee}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className={`p-5 text-center ${theme.textMuted} text-sm`}>
                          No programs listed for this college yet.
                        </div>
                      )}
                    </div>
                  )
                )}

                {/* Load More */}
                {page < totalPages && (
                  <div className={`${viewMode === 'grid' ? 'col-span-full' : ''} py-8 flex justify-center`}>
                    <button onClick={() => setPage(p => p + 1)}
                      className={`px-8 sm:px-10 py-3 rounded-full font-bold shadow-lg hover:-translate-y-1 transition-all flex items-center gap-2 group ${theme.card} ${theme.accentText}`}>
                      {loading ? <Loader2 className="animate-spin" /> : <>Load More <IconArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></>}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center p-8">
                <div className={`p-6 rounded-3xl shadow-xl border mb-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                  <Award className="w-16 h-16 sm:w-20 sm:h-20 text-yellow-500 mx-auto" />
                </div>
                <h2 className={`text-2xl sm:text-3xl font-bold mb-3 ${theme.textMain}`}>No Results Found</h2>
                <p className={`${theme.textMuted} text-center mb-8 text-sm sm:text-base`}>Try adjusting your search or filters.</p>
                <button onClick={clearAllFilters}
                  className="px-8 py-3 rounded-full font-bold text-white flex items-center gap-2 bg-gradient-to-r from-yellow-500 to-orange-500 hover:-translate-y-1 transition-all shadow-lg">
                  <RotateCcw className="w-4 h-4" /> Reset Search
                </button>
              </div>
            )}
          </div>

          {/* MOBILE FILTER BUTTON */}
          <button onClick={() => setMobileFilterOpen(true)}
            className="lg:hidden fixed bottom-6 right-6 z-50 text-white px-5 sm:px-6 py-3 sm:py-4 rounded-full shadow-2xl flex items-center gap-2 sm:gap-3 font-bold bg-gradient-to-r from-yellow-500 to-orange-500 hover:scale-105 active:scale-95 transition-all">
            <SlidersHorizontal className="w-5 h-5" />
            <span className="text-sm sm:text-base">Filters</span>
          </button>
        </main>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background-color: rgba(234, 179, 8, 0.3); border-radius: 20px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: rgba(234, 179, 8, 0.6); }
      `}</style>
    </div>
  );
}